/**
 * Pure display helpers for the feed fields added in Task 6/7 (employer type,
 * benefit chips, company initials) — global-constraints "Company type
 * labels" and "Benefits". Kept independent of Angular so they're trivially
 * unit-tested (mirrors `OtherLocationsLabel`/`job-card.component.ts`).
 */

/** global-constraints "Company type labels" table — anything else passes through verbatim; `null` means no tag. */
const COMPANY_TYPE_LABELS: Record<string, string> = {
    'Government Agency': 'Government',
    Educational: 'Academic',
    Nonprofit: 'Nonprofit',
    'Public Company': 'Public company',
    'Privately Held': 'Private company',
    Partnership: 'Private company',
    'Self-Owned': 'Private company',
    'Self-Employed': 'Private company',
};

/** "Government" / "Academic" / "Private company" / verbatim / null — the employer-type tag shown on the job card and detail view. */
export function CompanyTypeLabel(value: string | null): string | null {
    if (value === null) return null;
    return COMPANY_TYPE_LABELS[value] ?? value;
}

export interface BenefitChipsResult {
    Visible: string[];
    More: number;
}

/** First character upper-cased, the rest kept as-is — not a full title-case transform. */
function SentenceCase(value: string): string {
    if (value.length === 0) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Sentence-cased, de-duplicated (case-insensitive, feed order), capped at
 * `shown` (default 10) with the remainder counted into `More`. The worker
 * (Task 2) does not trim individual benefit strings, so blanks are dropped
 * and every entry is trimmed here before de-duplication.
 */
export function BenefitChips(list: readonly string[], shown = 10): BenefitChipsResult {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const raw of list) {
        const trimmed = raw.trim();
        if (trimmed.length === 0) continue;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(SentenceCase(trimmed));
    }
    return { Visible: unique.slice(0, shown), More: Math.max(0, unique.length - shown) };
}

/**
 * Leading/trailing punctuation (parens, dashes, periods, the em dash, …) —
 * never the letters/digits in between. Unicode-aware (`\p{L}`/`\p{N}` with
 * the `u` flag) so a non-ASCII leading/trailing letter ("Øresund", "École")
 * is never mistaken for punctuation and stripped — ASCII-only
 * `[^a-zA-Z0-9]` did exactly that (review finding, round 2).
 */
const LEADING_NON_ALNUM = /^[^\p{L}\p{N}]+/u;
const TRAILING_NON_ALNUM = /[^\p{L}\p{N}]+$/u;

/** The word's first code point, upper-cased — `Array.from` (not `charAt`/`slice`) so a character outside the BMP is never split across its surrogate pair. */
function FirstCodePoint(word: string): string {
    return (Array.from(word)[0] ?? '').toUpperCase();
}

/**
 * Two-letter avatar initials for a company name: the first letters of the
 * first two whitespace-split words, upper-cased, after stripping each word's
 * leading/trailing punctuation ("(VES) Consulting" → "VC", not "(C" —
 * review finding) and dropping any word that strips down to nothing (a bare
 * "—" token). A single surviving word gives its first two letters; blank,
 * null, or no surviving word gives `'?'`. Deliberately independent of
 * `PersonInitials` (person-card.component.ts) — different source field,
 * different fallback (`'?'` vs none needed there).
 */
export function Initials(name: string | null): string {
    const trimmed = name?.trim() ?? '';
    if (trimmed.length === 0) return '?';
    const words = trimmed
        .split(/\s+/)
        .map((word) => word.replace(LEADING_NON_ALNUM, '').replace(TRAILING_NON_ALNUM, ''))
        .filter((word) => word.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) return Array.from(words[0]).slice(0, 2).join('').toUpperCase();
    return FirstCodePoint(words[0]) + FirstCodePoint(words[1]);
}
